import {
  Component,
  Input,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl } from '@angular/forms';

import { FieldGroup, FieldConfig, getColorNameFromHex, SelectOption } from '../../interfaces/field-config.interface';
import { FieldRendererComponent } from '../field-renderer/field-renderer.component';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';

/**
 * GroupRendererComponent
 *
 * Renders a group of fields within a FormSectionComponent.
 * Simplified version without signals for better compatibility with OnPush change detection.
 */
@Component({
  selector: 'app-group-renderer',
  standalone: true,
  imports: [
    CommonModule,
    FormSectionComponent,
    FieldRendererComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (group && form) {
      <app-form-section
        [title]="group.name"
        [icon]="groupIcon"
        [iconColor]="groupIconColor"
        class="mb-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          @for (field of visibleFields; track trackByFieldId($index, field)) {
            @if (getControl(field.name); as control) {
              <div [class]="field.type === 'form' || field.type === 'attachment' ? 'md:col-span-2' : ''">
                <app-field-renderer
                  [config]="field"
                  [control]="control"
                  [externalOptions]="getExternalOptions(field.name)"
                  [formSubmitted]="formSubmitted"
                  [isExtension]="isExtension">
                </app-field-renderer>
              </div>
            }
          }
        </div>
      </app-form-section>
    }
  `
})
export class GroupRendererComponent {
  /**
   * Group configuration with fields
   */
  @Input({ required: true }) group!: FieldGroup;

  /**
   * Parent FormGroup containing all controls
   */
  @Input({ required: true }) form!: FormGroup;

  /**
   * Map of external options for select fields
   * Key is the field name, value is the options array
   */
  @Input() externalOptionsMap: Map<string, SelectOption[]> = new Map();

  /**
   * Flag indicating if the form has been submitted
   * Used to control when validation errors are displayed
   */
  @Input() formSubmitted: boolean = false;

  /**
   * Flag indicating if this is a credit extension (vs new opening)
   * Passed to field-renderer for attachment filtering
   */
  @Input() isExtension: boolean = false;

  /**
   * Get visible fields from the group
   */
  get visibleFields(): FieldConfig[] {
    return this.group?.fields?.filter(field => field.visible !== false) || [];
  }

  /**
   * Get icon for the group
   */
  get groupIcon(): string {
    return this.group?.icon || 'folder';
  }

  /**
   * Get icon color name from hex
   */
  get groupIconColor(): string {
    return getColorNameFromHex(this.group?.iconColor);
  }

  /**
   * Get FormControl for a field by name
   * Returns null if not found
   */
  getControl(fieldName: string): FormControl<any> | null {
    if (!this.form) return null;
    const control = this.form.get(fieldName);
    return control as FormControl<any> || null;
  }

  /**
   * Get external options for a field
   */
  getExternalOptions(fieldName: string): SelectOption[] {
    return this.externalOptionsMap?.get(fieldName) || [];
  }

  /**
   * Track function for fields - uses _id if available, falls back to index
   */
  trackByFieldId(index: number, field: FieldConfig): string | number {
    return field._id || index;
  }
}
